import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  isDarkMode: boolean;
  setThemeMode: (mode: ThemeMode) => void;
}

function systemIsDarkMode() {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedMode = window.localStorage.getItem('themeMode');

  if (storedMode === 'light' || storedMode === 'dark' || storedMode === 'system') {
    return storedMode;
  }

  if (storedMode) {
    window.localStorage.removeItem('themeMode');
  }

  return 'light';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => resolveStoredThemeMode());
  const [isDarkMode, setIsDarkMode] = useState(
    () => mode === 'dark' || (mode === 'system' && systemIsDarkMode())
  );

  const setThemeMode = (nextMode: ThemeMode) => {
    setMode(nextMode);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('themeMode', nextMode);
    }
  };

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const shouldUseDark = mode === 'dark' || (mode === 'system' && systemIsDarkMode());
    document.documentElement.classList.toggle('dark', shouldUseDark);
    setIsDarkMode(shouldUseDark);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleColorSchemeChange = (event: MediaQueryListEvent) => {
      document.documentElement.classList.toggle('dark', event.matches);
      setIsDarkMode(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleColorSchemeChange);

      return () => {
        mediaQuery.removeEventListener('change', handleColorSchemeChange);
      };
    }

    mediaQuery.addListener(handleColorSchemeChange);

    return () => {
      mediaQuery.removeListener(handleColorSchemeChange);
    };
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, isDarkMode, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
